import { Component, inject, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ChargeItemDefinition, Medication, ServiceRequest, Bundle } from 'fhir/r4';
import { backendEndPointToken } from '../../app.config';
import { BehaviorSubject, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { NaPipe } from '../na.pipe';
import { ChipsDirective } from '../../chips.directive';

interface PriceBreakdown {
    resourceId: string;
    resourceDisplay: string;
    totalSellingPrice: number;
    totalCostPrice?: number;
    currency: string;
    components: {
        base?: number;
        surcharge?: number;
        deduction?: number;
        discount?: number;
        tax?: number;
    };
    costComponents?: {
        base?: number;
        surcharge?: number;
        deduction?: number;
        discount?: number;
        tax?: number;
    };
    chargeItemDefId?: string;
    status?: string;
}

type ResourceType = 'medication' | 'service';

@Component({
    selector: 'app-price-display',
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatDividerModule,
        MatTooltipModule,
        NaPipe,
        ChipsDirective
    ],
    templateUrl: './price-display.component.html',
    styleUrls: ['./price-display.component.scss']
})
export class PriceDisplayComponent implements OnInit, OnChanges {
    @Input() resourceType: ResourceType = 'medication';
    @Input() resources: (Medication | ServiceRequest)[] = [];
    @Input() showBreakdown = false;
    @Input() compactMode = false;

    private http = inject(HttpClient);
    private backendUrl = inject(backendEndPointToken);

    loading$ = new BehaviorSubject<boolean>(false);
    prices$ = new BehaviorSubject<PriceBreakdown[]>([]);
    error$ = new BehaviorSubject<string | null>(null);

    ngOnInit(): void {
        this.fetchPrices();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['resources'] || changes['resourceType']) {
            this.fetchPrices();
        }
    }

    private fetchPrices(): void {
        if (!this.resources?.length) {
            this.prices$.next([]);
            return;
        }

        this.loading$.next(true);
        this.error$.next(null);

        // Fetch all ChargeItemDefinitions
        this.http.get<Bundle<ChargeItemDefinition>>(`${this.backendUrl}/ChargeItemDefinition?_count=500`)
            .pipe(
                map(bundle => bundle.entry?.map(e => e.resource as ChargeItemDefinition) || []),
                catchError(err => {
                    console.error('Failed to fetch ChargeItemDefinitions:', err);
                    this.error$.next('Failed to load pricing information');
                    return of([]);
                })
            )
            .subscribe(chargeItems => {
                const priceBreakdowns = this.resources
                    .map(resource => this.matchAndExtractPrice(resource, chargeItems))
                    .filter(Boolean) as PriceBreakdown[];

                this.prices$.next(priceBreakdowns);
                this.loading$.next(false);
            });
    }

    private matchAndExtractPrice(
        resource: Medication | ServiceRequest,
        chargeItems: ChargeItemDefinition[]
    ): PriceBreakdown | null {
        const resourceId = resource.id;
        if (!resourceId) return null;

        let matchedChargeItem: ChargeItemDefinition | undefined;

        if (this.resourceType === 'medication') {
            // Match by instance reference for Medication
            matchedChargeItem = chargeItems.find(ci =>
                ci.instance?.some(inst => inst.reference === `Medication/${resourceId}`)
            );
        } else {
            // Match by code for ServiceRequest
            const srCode = (resource as ServiceRequest).code?.coding?.[0]?.code ||
                (resource as ServiceRequest).code?.text;
            if (srCode) {
                matchedChargeItem = chargeItems.find(ci =>
                    ci.code?.coding?.[0]?.code === srCode ||
                    ci.code?.text === srCode
                );
            }
        }

        if (!matchedChargeItem) return null;

        return this.extractPriceBreakdown(resource, matchedChargeItem);
    }

    private extractPriceBreakdown(
        resource: Medication | ServiceRequest,
        chargeItem: ChargeItemDefinition
    ): PriceBreakdown {
        const resourceDisplay = this.getResourceDisplay(resource);
        const priceComponents = chargeItem.propertyGroup?.[0]?.priceComponent || [];

        const components = {
            base: 0,
            surcharge: 0,
            deduction: 0,
            discount: 0,
            tax: 0
        };

        const currency = priceComponents[0]?.amount?.currency || 'NGN';

        priceComponents.forEach(pc => {
            const value = pc.amount?.value || 0;
            const type = pc.type?.toLowerCase();

            switch (type) {
                case 'base':
                    components.base += value;
                    break;
                case 'surcharge':
                    components.surcharge += value;
                    break;
                case 'deduction':
                    components.deduction += value;
                    break;
                case 'discount':
                    components.discount += value;
                    break;
                case 'tax':
                    components.tax += value;
                    break;
            }
        });

        const totalSellingPrice =
            components.base +
            components.surcharge +
            components.tax -
            components.deduction -
            components.discount;

        // Extract cost price from extension
        const costComponents = this.extractCostComponents(chargeItem);
        const totalCostPrice = costComponents ?
            (costComponents.base || 0) +
            (costComponents.surcharge || 0) +
            (costComponents.tax || 0) -
            (costComponents.deduction || 0) -
            (costComponents.discount || 0) : undefined;

        return {
            resourceId: resource.id || '',
            resourceDisplay,
            totalSellingPrice,
            totalCostPrice,
            currency,
            components,
            costComponents,
            chargeItemDefId: chargeItem.id,
            status: chargeItem.status
        };
    }

    private extractCostComponents(chargeItem: ChargeItemDefinition): PriceBreakdown['costComponents'] | undefined {
        if (!chargeItem.extension?.length) return undefined;

        const totalCostExt = chargeItem.extension.find(ext =>
            ext.url === 'http://hl7.org/fhir/StructureDefinition/ChargeItemDefinition-totalCost'
        );

        if (!totalCostExt?.extension) return undefined;

        const costComponents = {
            base: 0,
            surcharge: 0,
            deduction: 0,
            discount: 0,
            tax: 0
        };

        totalCostExt.extension.forEach(subExt => {
            const typeExt = subExt.extension?.find(e => e.url === 'type');
            const amountExt = subExt.extension?.find(e => e.url === 'amount');

            if (!typeExt || !amountExt) return;

            const type = (typeExt as any).valueCode?.toLowerCase();
            const value = (amountExt as any).valueMoney?.value || 0;

            switch (type) {
                case 'base':
                    costComponents.base += value;
                    break;
                case 'surcharge':
                    costComponents.surcharge += value;
                    break;
                case 'deduction':
                    costComponents.deduction += value;
                    break;
                case 'discount':
                    costComponents.discount += value;
                    break;
                case 'tax':
                    costComponents.tax += value;
                    break;
            }
        });

        return costComponents;
    }

    private getResourceDisplay(resource: Medication | ServiceRequest): string {
        if (this.resourceType === 'medication') {
            const med = resource as Medication;
            return med.code?.text ||
                med.code?.coding?.[0]?.display ||
                `Medication ${med.id}`;
        } else {
            const sr = resource as ServiceRequest;
            return sr.code?.text ||
                sr.code?.coding?.[0]?.display ||
                `Service Request ${sr.id}`;
        }
    }

    formatCurrency(amount: number, currency: string): string {
        return new Intl.NumberFormat('en-NG', {
            style: 'currency',
            currency: currency || 'NGN'
        }).format(amount);
    }

    getTotalPrice(): number {
        const prices = this.prices$.getValue();
        return prices.reduce((sum, p) => sum + p.totalSellingPrice, 0);
    }

    getTotalCost(): number {
        const prices = this.prices$.getValue();
        return prices.reduce((sum, p) => sum + (p.totalCostPrice || 0), 0);
    }
}
